import { effect } from "@preact/signals";
import * as preact from "preact";

import * as exportDialog from "exportDialog";
import SelectionActionList from "SelectionActionList";
import * as languageMap from "shared/languageMap";
import RemoteUserWordsStore from "stores/RemoteUserWordsStore";
import type { AppStores } from "stores/stores";
import SelectAllView from "views/SelectAllView";
import SelectionActionView from "views/SelectionActionView";
import SelectWholeCollectionView from "views/SelectWholeCollectionView";
import SortAndFilterMenu, { type MenuOption } from "views/SortAndFilterMenu";
import UndoDeleteView from "views/UndoDeleteView";

interface AppRouterLike {
  navigate(path: string, options?: { trigger?: boolean }): void;
}

interface RemoteWordListToolbarProps {
  model: RemoteUserWordsStore;
  stores: AppStores;
  appRouter: AppRouterLike;
  /** The list's filters, for screens without the sidebar showing them */
  filters?: {
    options: MenuOption[];
    selected: string;
    onSelect: (value: string) => void;
  };
}

interface RemoteWordListToolbarState {
  wordGroup?: unknown;
  sortBy: string;
}

export class RemoteWordListToolbar extends preact.Component<
  RemoteWordListToolbarProps,
  RemoteWordListToolbarState
> {
  private disposeSearchQueryChange?: () => void;
  private disposeSignalsEffect?: () => void;

  state: RemoteWordListToolbarState = {
    wordGroup: this.props.model.searchQuery.value.wordGroup,
    sortBy: this.props.model.searchQuery.value.sortBy,
  };

  componentDidMount() {
    this.disposeSearchQueryChange = this.props.model.onSearchQueryChange(() => {
      this.setState({
        wordGroup: this.props.model.searchQuery.value.wordGroup,
        sortBy: this.props.model.searchQuery.value.sortBy,
      });
    });
    this.disposeSignalsEffect = effect(() => {
      void this.props.model.totalCountSignal.value;
      this.forceUpdate();
    });
  }

  componentWillUnmount() {
    this.disposeSearchQueryChange?.();
    this.disposeSignalsEffect?.();
  }

  selectionActions() {
    return new SelectionActionList(
      [
        {
          id: "delete",
          name: "Delete",
          destructive: true,
          applyQuery: {
            $and: [
              { deleted: { $ne: true } },
              { wordGroup: { $ne: "deleted" } },
            ],
          },
        },
        {
          id: "undelete",
          name: "Un-Delete",
          applyQuery: {
            $or: [{ deleted: true }, { wordGroup: "deleted" }],
          },
        },
        {
          id: "permDelete",
          name: "Permanently Delete",
          destructive: true,
          applyQuery: {
            $or: [{ deleted: true }, { wordGroup: "deleted" }],
          },
        },
        {
          id: "export",
          name: "Export",
          exec: () => exportDialog.show(this.props.model, this.props.stores),
        },
        {
          id: "flashcards",
          name: "Practice Words",
          exec: () => {
            const firstWord = this.props.model.firstWord();
            if (!firstWord) return;
            this.props.appRouter.navigate(
              "/" +
                firstWord.state.value.language +
                "/flashcards/word/" +
                this.props.model.selectionStore.itemIDs.value.join("|"),
              { trigger: true }
            );
          },
        },
      ],
      { selectionStore: this.props.model.selectionStore }
    );
  }

  sortByOptions(): MenuOption[] {
    const sortByOptions = [
      {
        value: "lastModified",
        label: "Recently Modified",
      },
      {
        value: "lastPracticed",
        label: "Recently Practiced",
      },
      {
        value: "new",
        label: "Recently Added",
      },
      {
        value: "old",
        label: "Oldest",
      },
    ];

    if (
      languageMap.map[this.props.stores.user.currentLearningLanguage.value]
        .frequencies
    ) {
      return sortByOptions.concat([
        {
          value: "frequency",
          label: "Usefulness / Frequency",
        },
        {
          value: "relevancy",
          label: "Relevancy",
        },
      ]);
    }

    return sortByOptions;
  }

  changeSortBy = (sortBy: string) => {
    this.props.model.patchSearchQuery({ sortBy });
  };

  render() {
    const showSortBy = this.state.wordGroup !== "deleted";
    const totalCount = this.props.model.totalCount;
    const selectionStore = this.props.model.selectionStore;

    return (
      <>
        <div class="listControls">
          <div class="checkboxCell selectAllContainer">
            <SelectAllView model={selectionStore} />
          </div>
          <SelectionActionView
            model={selectionStore}
            selectionActionList={this.selectionActions()}
          />
          <div class="spacer"></div>
          <span class="wordCount">
            {totalCount.toLocaleString()} {totalCount === 1 ? "word" : "words"}
          </span>
          {showSortBy || this.props.filters ? (
            <SortAndFilterMenu
              sortOptions={showSortBy ? this.sortByOptions() : []}
              sortBy={this.state.sortBy}
              onSort={this.changeSortBy}
              filters={this.props.filters}
            />
          ) : null}
        </div>
        <SelectWholeCollectionView model={this.props.model.selectionStore} />
        <UndoDeleteView model={this.props.model} />
      </>
    );
  }
}
